import React from 'react';
import { Button } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ReChartComponentLine = (props) => {
  const pageSize = 10;
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [pageIndex, setPageIndex] = React.useState(0);
  
  const uniqueDatasets = props.data.datasets.map((dataset, index) => ({
    ...dataset,
    uniqueLabel: dataset.label || `Series ${index + 1}`,
    originalIndex: index
  }));
  
  const deduplicatedDatasets = uniqueDatasets.reduce((acc, dataset) => {
    const existingIndex = acc.findIndex(d => d.uniqueLabel === dataset.uniqueLabel);
    if (existingIndex === -1) {
      acc.push(dataset);
    }
    return acc;
  }, []);

  const transformData = () => {
    const { labels } = props.data;
    
    return labels.map((label, index) => {
      const item = { name: label };
      let allZeroOrNull = true;
      
      deduplicatedDatasets.forEach(dataset => {
        const value = dataset.data[index];
        
        if (value !== 0 && value !== "0" && value !== null && value !== undefined) {
          item[dataset.uniqueLabel] = value;
          allZeroOrNull = false;
        }
      });
      
      return allZeroOrNull ? null : item;
    }).filter(item => item !== null);
    
  };

  const colors = [
    '#1890FF', '#f95d6a', '#ffc658', '#82ca9d', '#a4de6c',
    '#d0ed57', '#ff8042', '#83a6ed', '#ff6e7f', '#a05195',
    '#d45087', '#8dd1e1', '#ff7c43', '#665191', '#a05195',
    '#003f5c', '#2f4b7c', '#665191', '#a05195', '#d45087'
  ];

  const chartData = transformData();
  
  
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #ccc',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
        }}>
            <p><b>{label}</b></p>
            <div style={{ display: 'grid', gap: '4px' }}>
                {payload.map((entry, index) => (
                <div key={`item-${index}`} style={{
                    color: entry.color,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <span className='me-2'>
                    {entry.name} :
                    </span>
                    <b>{formatNumber(entry.value)}</b>
                </div>
                ))}
            </div>
        </div>
      );
    }
    return null;
  };

  const datasetLabels = deduplicatedDatasets.map(dataset => dataset.uniqueLabel);
  const totalPages = Math.ceil(datasetLabels.length / pageSize) || 1;
  const startIndex = pageIndex * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleLabels = datasetLabels.slice(startIndex, endIndex);

  const handlePrev = () => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <ResponsiveContainer width="100%" height={props.height || 400}>
      <LineChart
        data={chartData}
        margin={{
          top: 20,
          right: isMobile ? 20 : 150, 
          bottom: isMobile ? 60 : 20,
          left: 20,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          layout={isMobile ? "horizontal" : "vertical"} 
          verticalAlign={isMobile ? "bottom" : "middle"} 
          align={isMobile ? "center" : "right"}
          wrapperStyle={{
            paddingLeft: isMobile ? 0 : '20px',
          }}
          content={(legendProps) => {
            const items = legendProps?.payload || [];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                <div style={isMobile 
                  ? { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }
                  : { display: 'grid', gap: 6 }
                }>
                  {items.map((entry, i) => (
                  <div key={`legend-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isMobile ? 6 : 0 }}>
                      <span style={{ width: 12, height: 12, backgroundColor: entry.color, display: 'inline-block' }} />
                      <span style={{ fontSize: 12 }}>{entry.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                  <Button shape="circle" size="small" onClick={handlePrev} disabled={pageIndex === 0} icon={<UpOutlined />} />
                  <Button shape="circle" size="small" onClick={handleNext} disabled={pageIndex >= totalPages - 1} icon={<DownOutlined />} />
                </div>
              </div>
            );
          }}
        />
        {visibleLabels.map((label, index) => {
          const datasetIndex = startIndex + index;
          const dataset = deduplicatedDatasets[datasetIndex];
          return (
            <Line
              key={`line-${dataset?.originalIndex || index}-${label}`}
              type="monotone"
              dataKey={label}
              dot={false}
              stroke={colors[datasetIndex % colors.length]}
              strokeWidth={1.5}
              activeDot={{ r: 5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ReChartComponentLine;